const WIDGET_ROLES = [
    'button',
    'checkbox',
    'combobox',
    'link',
    'listbox',
    'menuitem',
    'menuitemcheckbox',
    'menuitemradio',
    'option',
    'radio',
    'searchbox',
    'slider',
    'spinbutton',
    'switch',
    'tab',
    'textbox',
    'treeitem',
];

/** Native and claimed controls (focusable, ARIA widget roles): MUI renders some controls as spans and divs. */
const CONTROL_SELECTOR = [
    // An anchor counts even without `href`: lint rejects one, but it still takes clicks.
    'a, button, input, select, textarea, label, summary',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable]:not([contenteditable="false"])',
    // A claim can be wrong, e.g. a chip with `role="button"` that handles nothing.
    ...WIDGET_ROLES.map((role) => `[role="${role}"]`),
].join(', ');

type ClickEvent = { target: EventTarget | null; currentTarget: Element };

/** A label without a control, or a summary outside a `<details>`, does nothing on a click. */
function actsOnClick(element: Element): boolean {
    if (element instanceof HTMLLabelElement) {
        return element.control !== null;
    }
    if (element.localName === 'summary') {
        return element.parentElement?.localName === 'details';
    }
    return true;
}

function nestedControl({ target, currentTarget }: ClickEvent): Element | null {
    if (!(target instanceof Element) || !currentTarget.contains(target)) {
        return null;
    }
    let control = target.closest(CONTROL_SELECTOR);
    while (control !== null && control !== currentTarget && currentTarget.contains(control)) {
        if (actsOnClick(control)) {
            return control;
        }
        control = control.parentElement?.closest(CONTROL_SELECTOR) ?? null;
    }
    return null;
}

/** Inside `currentTarget` in the DOM: React bubbles a portal's events (a row's tooltip) through the tree. */
function isClickInside({ target, currentTarget }: ClickEvent): boolean {
    return target instanceof Element && currentTarget.contains(target);
}

/** A click on `currentTarget` itself, not on a nested control; a role-less clickable `div` must `stopPropagation()`. */
export function isOwnClick(event: ClickEvent): boolean {
    return isClickInside(event) && nestedControl(event) === null;
}
